import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";

import { AuthContext } from "../../AuthContext";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppField from "../../design-system/components/AppField";
import AppInput from "../../design-system/components/AppInput";
import AppLink from "../../design-system/components/AppLink";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./login.css";
import { EMPLOYEE_HOME_PATH, isEmployeeAccessLevel } from "../../utils/accessLevels";

const LOGO_URL = "/assets/TimeTab_Logo_quer_cmyk_pos.svg";
const CONTACT_URL = "https://timetab.ch/kontakt/";

const APP_STORE_URL = "https://apps.apple.com/ch/app/timetab-schichtplan/id6480352029?l=de-CH";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=ch.timetab&hl=de_CH";
const IOS_BADGE_URL = "https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg";
const ANDROID_BADGE_URL = "https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg";

// Einfacher UA-Check im Frontend: TimeTab blockt den mobilen Browser und verweist
// auf die App. Das Backend liefert dafuer keinen eigenen Fehlercode.
export const isMobileUA = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    (typeof navigator !== "undefined" ? navigator.userAgent : "") || ""
  );

export function BrandPanel() {
  return (
    <aside className="v2login__brand">
      <div className="v2login__brand-mark" aria-hidden="true">T</div>
      <div className="v2login__brand-logo">
        <img src={LOGO_URL} alt="TimeTab" />
      </div>
      <div className="v2login__brand-copy">
        <h1 className="v2login__brand-title">
          <span className="v2login__brand-strike">Schichtplan-Stress?</span>
          <br />
          Nie mehr. Versprochen.
        </h1>
        <p className="v2login__brand-subtitle">
          Bei Fragen melde dich{" "}
          <AppLink href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
            hier
          </AppLink>
          .
        </p>
      </div>
    </aside>
  );
}

// Verweis auf die TimeTab-App. Wird aus drei Richtungen genutzt: mobiler Browser
// auf /login, /registration und /app-only (Rollen ohne Web-Zugang). Titel/Text
// kommen deshalb von aussen, jeder Aufrufer bringt seine eigene Formulierung mit.
// showQr nur am Desktop sinnvoll — den Code auf dem eigenen Handy kann niemand scannen.
export function MobileAppNotice({ title, subtitle, showQr = false, qrHint, footer = null }) {
  const { t } = useTranslation();

  return (
    <div className="v2login__form">
      <div className="v2login__head">
        <h2 className="v2login__title">
          {title || t("login.mobile_block_title", "Bitte nutze die TimeTab App")}
        </h2>
        <p className="v2login__subtitle">
          {subtitle || t("login.mobile_block_sub", "Das Login über den mobilen Browser ist deaktiviert.")}
        </p>
      </div>

      {showQr && qrHint ? <p className="v2login__qr-hint">{qrHint}</p> : null}

      {showQr ? (
        <div className="v2login__qr-row">
          {[
            { url: APP_STORE_URL, caption: t("login.qr_ios", "iPhone") },
            { url: PLAY_STORE_URL, caption: t("login.qr_android", "Android") },
          ].map((store) => (
            <div className="v2login__qr" key={store.url}>
              {/* Bewusst feste Farben statt Design-Tokens: QR-Codes brauchen dunkle
                  Module auf hellem Grund, sonst erkennen viele Scanner sie nicht. */}
              <div className="v2login__qr-tile">
                <QRCodeSVG value={store.url} size={116} bgColor="#ffffff" fgColor="#111111" level="M" />
              </div>
              <span className="v2login__qr-caption">{store.caption}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="v2login__stores">
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("login.appstore_ios", "App Store für iOS")}
        >
          <img src={IOS_BADGE_URL} alt={t("login.appstore_ios", "App Store für iOS")} />
        </a>
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("login.playstore_android", "Google Play für Android")}
        >
          <img src={ANDROID_BADGE_URL} alt={t("login.playstore_android", "Google Play für Android")} />
        </a>
      </div>

      {footer ? <div className="v2login__notice-footer">{footer}</div> : null}
    </div>
  );
}

function LoginForm() {
  const { user, login, error, setError } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Erfolgsmeldung, wenn man von der Registrierung hierher kommt.
  const [flash, setFlash] = useState(location.state?.flash || "");
  const [flashVariant] = useState(location.state?.flashVariant || "success");
  useEffect(() => {
    if (!location.state?.flash) return undefined;
    navigate(location.pathname, { replace: true, state: {} });
    const timer = setTimeout(() => setFlash(""), 3500);
    return () => clearTimeout(timer);
  }, [location, navigate]);

  // Post-Login-Weiterleitung. Ein Token bekommt nur, wer Web-Zugang hat — alle
  // uebrigen Rollen landen schon in AuthContext.login auf /app-only.
  // Mitarbeitende starten im Schichtplan.
  useEffect(() => {
    if (!user) return;
    navigate(isEmployeeAccessLevel(user.access_level) ? EMPLOYEE_HOME_PATH : "/admin/dashboard");
  }, [user, navigate]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
    } catch {
      setError(t("login.error_invalid", "E-Mail-Adresse oder Passwort ist nicht korrekt."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="v2login__form v2login__fade-in" onSubmit={onSubmit}>
      <div className="v2login__logo">
        <img src={LOGO_URL} alt={t("login.logoAlt", "TimeTab Logo")} />
      </div>

      <div className="v2login__head">
        <h2 className="v2login__title">
          {t("login.welcome_back", "Schön, dass du wieder da bist.")}
        </h2>
        <p className="v2login__subtitle">Anmeldung mit deinem TimeTab-Konto</p>
      </div>

      {flash ? (
        <AppCallout
          variant={flashVariant}
          description={flash}
          style={{ marginBottom: "var(--ds-space-16)" }}
        />
      ) : null}

      <div className="v2login__fields">
        <AppField label={t("login.email", "Email")}>
          <AppInput
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="username"
            placeholder="name@firma.ch"
          />
        </AppField>
        <AppField label={t("login.password", "Passwort")}>
          <AppInput
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
          />
        </AppField>
      </div>

      <div className="v2login__row-between">
        <AppLink size="sm" href="/forget_password">
          {t("login.forgot_password", "Passwort Vergessen?")}
        </AppLink>
      </div>

      {error ? (
        <AppCallout
          variant="error"
          description={error}
          style={{ marginTop: "var(--ds-space-16)" }}
        />
      ) : null}

      <div className="v2login__actions">
        <AppButton
          type="submit"
          variant="primary"
          size="lg"
          className="v2login__submit"
          disabled={loading}
        >
          {loading ? t("login.submit_loading", "Anmelden…") : t("login.submit", "Anmelden")}
        </AppButton>
      </div>

      <div className="v2login__footnote">
        <AppLink size="sm" href="/registration">
          {t("login.registration", "Registriere dein Unternehmen")}
        </AppLink>
      </div>
    </form>
  );
}

export function LoginSplit({ children }) {
  return (
    <div className="v2login">
      <BrandPanel />
      <section className="v2login__panel">
        <div className="v2login__mobile-logo" aria-hidden="true">
          <img src={LOGO_URL} alt="" />
        </div>
        {children}
        <div className="v2login__mobile-footer" aria-hidden="true">
          <span>Schichtplan-Stress? Nie mehr. Versprochen.</span>
        </div>
      </section>
    </div>
  );
}

export default function Login() {
  const mobileBlocked = useMemo(() => isMobileUA(), []);

  return <LoginSplit>{mobileBlocked ? <MobileAppNotice /> : <LoginForm />}</LoginSplit>;
}
